/* Four cards: where they are, where they came from, who owns them, what was said.
 *
 * WHERE THEY CAME FROM AND HOW THEY HEARD ARE SHOWN APART. They answer different
 * questions and merging them loses the referral, which is the most valuable answer
 * on the enquiry form. See the note in lib/leads.
 *
 * OWNERSHIP NAMES THE ANCHOR, NOT THE ROUTING. `owner` is the desk's default and
 * falling back to it put "Coach Tomas W. · last connected yesterday" on a brand-new
 * enquiry whose track panel said, correctly, that nobody had spoken to them. Both
 * panels now read the same fact.
 */
import type { Lead } from '@acrostic/model';
import { Panel, NoneRow } from '../../components/Panel.js';
import { fmt } from '../../lib/fmt.js';
import { OPEN_PATH, STAGES, anchorOf, routedTo } from '../../lib/leads.js';

/** A label and a value, with the absent ones dropped rather than dashed: a card of
 *  nine dashes says nothing, and these cards are read at a glance.
 *
 *  An empty list renders NOTHING, not an empty-state line. Every caller here already
 *  follows the list with a sentence that says what is missing and why, and two of
 *  those stacked ("Nothing recorded." above "nobody has spoken to them yet") is the
 *  same absence reported twice. */
function Rows({ rows }: { rows: [string, string | null | undefined][] }) {
  const kept = rows.filter(([, v]) => v != null && v !== '');
  if (!kept.length) return null;
  return (
    <div className="deflist">
      {kept.map(([k, v]) => (
        <div className="defrow" key={k}>
          <span className="t-caption muted">{k}</span>
          <span className="t-body-small">{v}</span>
        </div>
      ))}
    </div>
  );
}

function Pipeline({ l }: { l: Lead }) {
  const at = OPEN_PATH.indexOf(l.stage);
  const terminal = at === -1;
  const since = [...l.stageHistory].reverse().find((h) => h.stage === l.stage)?.at ?? null;
  return (
    <Panel title="Pipeline">
      {terminal ? (
        <p className="t-body-small">
          <span className={'badge ' + STAGES[l.stage].tone + ' t-label-small'}>{STAGES[l.stage].long}</span>
          <span className="muted" style={{ marginLeft: 8 }}>
            {l.stage === 'won' ? 'Converted to a member.' : 'Closed without converting.'}
          </span>
        </p>
      ) : (
        <div className="stagepath">
          {OPEN_PATH.map((k, i) => (
            <span
              key={k}
              className={'badge t-label-xsmall ' + (i === at ? STAGES[k].tone : i < at ? 'good' : 'ahead')}
            >{i < at ? '✓ ' : ''}{STAGES[k].short}</span>
          ))}
        </div>
      )}
      <p className="t-caption muted" style={{ marginTop: 10 }}>
        Created {fmt.date(l.createdAt)}{since ? ` · in this stage since ${fmt.date(since)}` : ''}
      </p>
      {l.nextAction && (
        <p className="t-body-small" style={{ marginTop: 6 }}><strong>Next action: </strong>{l.nextAction}</p>
      )}
    </Panel>
  );
}

function Ownership({ l }: { l: Lead }) {
  const anchor = anchorOf(l);
  const routed = routedTo(l);
  const last = [...l.notes].sort((a, b) => b.at.localeCompare(a.at))[0];
  /* "Last connected" is a PERSON speaking to them. With no anchor and no note there
     has been no conversation, and the enquiry landing is not one. */
  const when = last ? last.at : (anchor ? l.lastActivity : null);
  return (
    <Panel title="Ownership">
      <Rows rows={[
        ['Support coach', anchor],
        ['Last connected', when ? fmt.ago(when) : null],
      ]} />
      <p className="t-body-small muted" style={{ marginTop: 8 }}>
        {last?.text
          ? last.text
          : anchor ? 'Nothing logged about the last conversation.'
          : routed ? `Routed to ${routed} · nobody has spoken to them yet.`
          : 'No one here has spoken to them yet.'}
      </p>
    </Panel>
  );
}

export function LeadOverview({ l, onNote }: { l: Lead; onNote: () => void }) {
  const notes = [...l.notes].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 4);
  return (
    <div className="grid2">
      <Pipeline l={l} />

      <Panel title="Where they came from">
        <Rows rows={[
          ['Lead source', l.source],
          ['Subcategory', l.sourceDetail],
          ['Heard about us via', l.heard],
          ['Interest', l.interest],
          ['Est. value', l.valueCents == null ? null : fmt.money(l.valueCents) + '/mo'],
        ]} />
        {!l.source && !l.heard && (
          <NoneRow>Nobody asked how they found you. It is the one question the enquiry form is for.</NoneRow>
        )}
      </Panel>

      <Ownership l={l} />

      <Panel
        title="Recent notes"
        right={<button type="button" className="btn t-label-small" onClick={onNote}>Add note</button>}
      >
        {!notes.length ? <NoneRow>No notes yet.</NoneRow> : (
          <div className="rows">
            {notes.map((n, i) => (
              <div className="row" key={`${n.at}-${i}`}>
                <div className="who">
                  <div className="n t-body-medium">{n.text}</div>
                  <div className="s t-caption muted">{[n.who, fmt.ago(n.at)].filter(Boolean).join(' · ')}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
